import Page from '@/components/page'
import type { InferGetServerSidePropsType } from 'next'
import { getFooter, graphQLClient } from 'src/lib/graphql-contentful-api'
import type { Options } from '@contentful/rich-text-react-renderer'
import { BLOCKS } from '@contentful/rich-text-types'
import type { Document } from '@contentful/rich-text-types'
import type {
  ComposeComponentTextFragment,
  LinkNode,
} from 'src/lib/rich-text-render-helpers'
import {
  ComponentRenderer,
  ComposeComponentTextList_Fragment,
  ComposeComponentText_Fragment,
  createEntryMap,
} from 'src/lib/rich-text-render-helpers'
import type { FragmentType } from '.gql/fragment-masking'
import { useFragment as getFragment } from '.gql/fragment-masking'
import PageMetadata from '@/components/metadata'
import { graphql } from '.gql/gql'
import invariant from 'tiny-invariant'
import type { ServerSideProps } from '@/pages/index'

export const SoftwareRequirements_Fragment = graphql(/* GraphQL */ `
  fragment SoftwareRequirements_Fragment on WritingLegendsMarketingSiteTechReqPageSoftwareRequirements {
    __typename
    json
    links {
      entries {
        block {
          sys {
            id
            __typename
          }
          ... on ComposeComponentTextList {
            ...ComposeComponentTextList_Fragment
          }
        }
      }
    }
  }
`)

export const WritingLegendsTechnicalRequirementsPage_Query = graphql(
  /* GraphQL */ `
    query writingLegendsMarketingSiteTechReqPage(
      $id: String!
      $locale: String
    ) {
      writingLegendsMarketingSiteTechReqPage(id: $id, locale: $locale) {
        sys {
          id
        }
        seoPageTitle
        seoPageDescription
        seoKeywords
        softwareRequirements {
          json
          ...SoftwareRequirements_Fragment
        }
      }
    }
  `,
)

export const getServerSideProps = async ({ query, req }: ServerSideProps) => {
  invariant(query.locale, 'query.locale is required')

  const [{ data: footerData }, { data: techReqData }] = await Promise.all([
    getFooter(query.locale),
    graphQLClient.query(WritingLegendsTechnicalRequirementsPage_Query, {
      id: '52hGpJ9xRMcreGkZK4cts2',
      locale: query.locale,
    }),
  ])

  if (
    !footerData?.composeComponentWlFooterBlock ||
    !techReqData?.writingLegendsMarketingSiteTechReqPage ||
    !techReqData.writingLegendsMarketingSiteTechReqPage.softwareRequirements
  ) {
    return { notFound: true }
  }

  return {
    props: {
      footerProps: footerData.composeComponentWlFooterBlock,
      techReqProps: techReqData.writingLegendsMarketingSiteTechReqPage,
      softwareRequirements:
        techReqData.writingLegendsMarketingSiteTechReqPage.softwareRequirements,
      country: query.country,
      domain: req.headers.host,
    },
  }
}

export type SoftwareRequirements = FragmentType<
  typeof SoftwareRequirements_Fragment
>

export function TextBlock({ query }: { query: ComposeComponentTextFragment }) {
  const { text } = getFragment(ComposeComponentText_Fragment, query)

  return (
    <ComponentRenderer
      classNames="mb-4 w-full prose-h4:text-lg prose-h4:font-bold md:w-1/2"
      document={text?.json as Document}
    />
  )
}

function TechnicalRequirements({
  footerProps,
  techReqProps,
  softwareRequirements,
  country,
  domain,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { seoKeywords, seoPageDescription, seoPageTitle } = techReqProps
  const data = getFragment(SoftwareRequirements_Fragment, softwareRequirements)
  const entryMap = createEntryMap(data.links.entries.block)

  const renderOptions: Options = {
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY]: (node: LinkNode) => {
        const id = node.data?.target?.sys?.id ?? ''
        const entry = entryMap.get(id)

        if (entry?.__typename !== 'ComposeComponentTextList') return

        const { textItemsCollection } = getFragment(
          ComposeComponentTextList_Fragment,
          entry,
        )

        return (
          <div className="mb-4 flex w-full flex-col gap-12 md:flex-row">
            {textItemsCollection?.items.map(
              (item) => item && <TextBlock query={item} key={item.sys.id} />,
            )}
          </div>
        )
      },
    },
  }

  return (
    <>
      <PageMetadata
        description={seoPageDescription}
        keywords={seoKeywords}
        title={seoPageTitle}
      />
      <Page footerData={footerProps} country={country} domain={domain}>
        <ComponentRenderer
          classNames="mx-auto max-w-screen-lg space-y-6 px-8 py-8 prose-headings:text-left prose-h4:text-base prose-h4:text-black"
          document={softwareRequirements.json as Document}
          options={renderOptions}
        />
      </Page>
    </>
  )
}

export default TechnicalRequirements
